import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import spacing from '../spacing';
import Button from './Button';
import Card, { CardContent } from './Card';

/**
 * ErrorBoundary Component
 * @component
 * @description Catches JavaScript errors in child components and displays a fallback UI
 * 
 * @example
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * 
 * @example
 * // Custom fallback
 * <ErrorBoundary fallback={<CustomError />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */

// Renders the default fallback UI. Extracted to a function component so it
// can read the active theme (light/dark) via useTheme() — the class-based
// ErrorBoundary below has no hook access, but it always stays legible because
// every color here comes from theme.palette instead of hardcoded hex values.
const ErrorBoundaryFallback = ({ title, message, error, errorInfo, onReset, showReloadButton }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: spacing[4],
      }}
    >
      <Card sx={{ maxWidth: 600 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: spacing[3],
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.error.main, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 48,
                  color: theme.palette.error.main,
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  marginBottom: spacing[2],
                }}
              >
                {title || 'Algo salió mal'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  marginBottom: spacing[3],
                }}
              >
                {message ||
                  'Ha ocurrido un error inesperado. Por favor, intenta recargar la página.'}
              </Typography>
            </Box>

            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  width: '100%',
                  padding: spacing[3],
                  backgroundColor: 'action.hover',
                  borderRadius: spacing.borderRadius.md,
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: theme.palette.error.main,
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <strong>Error:</strong> {error.toString()}
                <br />
                <br />
                <details>
                  <summary style={{ cursor: 'pointer' }}>Stack trace</summary>
                  <pre style={{ marginTop: spacing[2], fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                    {errorInfo?.componentStack}
                  </pre>
                </details>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: spacing[2] }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={onReset}
              >
                Reintentar
              </Button>
              {showReloadButton !== false && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => window.location.reload()}
                >
                  Recargar página
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
    }

    // Here you could also log to an error reporting service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI — delegates to ErrorBoundaryFallback so the colors
      // stay theme-aware (light/dark) via useTheme().
      return (
        <ErrorBoundaryFallback
          title={this.props.title}
          message={this.props.message}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          showReloadButton={this.props.showReloadButton}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
